import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { FilledButton, Alert } from '@ds/react-components';
import { isNegativeNumber } from 'utils/misc';
import {
  convertToInteger,
  convertToFloat,
  roundToTwoDecimals,
} from 'utils/numbers';
import PageWrapper from 'components/ui/PageWrapper';
import * as Styled from './SuspensionRuleDetail.styled';
import {
  fetchSuspensionRule,
  updateSuspensionRule,
} from '../../../../services/suspensionRules';

const SuspensionRuleDetail = () => {
  const history = useHistory();
  const { facilityAccountId } = useParams();
  const { handleSubmit, control, errors, reset } = useForm({
    defaultValues: {
      fee: null,
      duration: null,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const [suspensionRule, setSuspensionRule] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [submissionError, setSubmissionError] = useState('');

  useEffect(() => {
    const getSuspensionRule = async (facilityAccountId) => {
      try {
        setIsLoading(true);
        const result = await fetchSuspensionRule(facilityAccountId);
        reset({ fee: result.fee, duration: result.duration });
        setSuspensionRule(result);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.warn(error);
      }
    };

    getSuspensionRule(facilityAccountId);
  }, [facilityAccountId, reset]);

  const validateNegativeNumber = (number) => {
    return !isNegativeNumber(number);
  };

  const onClearAll = () => {
    reset({ fee: '', duration: '' });
  };

  const onSubmit = async (data) => {
    const { fee, duration } = data;
    const submittingData = {
      fee: fee,
      duration: duration,
      facilityAccountId,
    };

    try {
      setIsSubmittingForm(true);
      await updateSuspensionRule(submittingData);
      setSubmissionError('');
      history.push('/SuspensionRules');
    } catch (error) {
      setIsSubmittingForm(false);
      setSubmissionError(
        error.message || 'Something went wrong. Please try again.'
      );
      console.error(error);
    }
  };

  const onBack = () => {
    history.push(`/SuspensionRules`);
  };

  if (isLoading) {
    return (
      <PageWrapper>
        <Styled.Paper>
          <Styled.PaperHeader>
            <Styled.LoaderHeader />
          </Styled.PaperHeader>
          <Styled.PaperBody>
            <Styled.SuspensionRuleLoaderContainer>
              <Styled.SuspensionRuleLoader />
            </Styled.SuspensionRuleLoaderContainer>
          </Styled.PaperBody>
        </Styled.Paper>
      </PageWrapper>
    );
  }

  if (!suspensionRule) {
    return (
      <PageWrapper>
        <Styled.InfoAlert variant={'warning'} dismissible={false}>
          <Alert.AlertMessage>
            {
              'The supension rule does not exist or you do not have permission to access it.'
            }
          </Alert.AlertMessage>
        </Styled.InfoAlert>
        <Styled.ActionButtonContainer>
          <FilledButton type={'none'} onClick={onBack}>
            {'Back'}
          </FilledButton>
        </Styled.ActionButtonContainer>
      </PageWrapper>
    );
  }

  return (
    suspensionRule && (
      <PageWrapper>
        {!!submissionError && (
          <Styled.InfoAlert
            variant={'error'}
            role={'banner'}
            dismissible={false}>
            <Alert.AlertMessage data-testid={`submission-error`}>
              {submissionError}
            </Alert.AlertMessage>
          </Styled.InfoAlert>
        )}
        <Styled.BusinessAccountName>
          {`${suspensionRule.businessName} - ${suspensionRule.businessId}`}
        </Styled.BusinessAccountName>
        <Styled.FormContainer>
          <Styled.Paper>
            <Styled.PaperHeader>
              <Styled.PaperHeaderText>
                {'Edit Suspension Rule'}
              </Styled.PaperHeaderText>
              <Styled.ClearAllButton onClick={onClearAll}>
                Clear all
              </Styled.ClearAllButton>
            </Styled.PaperHeader>
            <Styled.PaperBodyOverload>
              <Styled.InputWrapper>
                <Controller
                  name="fee"
                  control={control}
                  rules={{
                    required: false,
                    max: {
                      value: 10000,
                      message: 'Fee cannot be greater than 10000.',
                    },
                    validate: validateNegativeNumber,
                  }}
                  render={({ onChange, name, value, onBlur }) => (
                    <>
                      <Styled.LabelContainer>
                        <Styled.InputLabel id={'fee'}>
                          {'Suspension fee (Weekly) '}
                        </Styled.InputLabel>
                      </Styled.LabelContainer>
                      <Styled.FormInput
                        name={name}
                        value={value}
                        labelId={'fee'}
                        type={'number'}
                        step={0.01}
                        min={1.0}
                        onBlur={(event) => {
                          onBlur(event);
                          onChange(roundToTwoDecimals(event.target.value));
                        }}
                        onChange={(event) => {
                          onChange(convertToFloat(event.target.value));
                        }}
                        startAdornment={'$'}
                        showStartAdornmentDivider={true}
                        showInlineIcons={false}
                        error={
                          errors.fee &&
                          (errors.fee.message || errors.fee.type === 'validate')
                        }
                      />
                      {errors.fee && errors.fee.type === 'validate' && (
                        <Styled.ErrorMessage>
                          {'Fee cannot be less than 0.'}
                        </Styled.ErrorMessage>
                      )}
                    </>
                  )}
                />
              </Styled.InputWrapper>
              <Styled.InputWrapper>
                <Controller
                  name="duration"
                  control={control}
                  rules={{
                    required: false,
                    max: {
                      value: 52,
                      message: 'Duration cannot be greater than 52 weeks.',
                    },
                    validate: validateNegativeNumber,
                  }}
                  render={({ onChange, name, value, onBlur }) => (
                    <>
                      <Styled.LabelContainer>
                        <Styled.InputLabel id={'duration'}>
                          {'Allowed supension period (Weeks) '}
                        </Styled.InputLabel>
                      </Styled.LabelContainer>
                      <Styled.FormInput
                        name={name}
                        value={value}
                        labelId={'duration'}
                        type={'number'}
                        step={1}
                        min={0}
                        onBlur={(event) => {
                          onBlur(event);
                        }}
                        onChange={(event) => {
                          onChange(convertToInteger(event.target.value));
                        }}
                        showInlineIcons={false}
                        error={
                          errors.duration &&
                          (errors.duration.message ||
                            errors.duration.type === 'validate')
                        }
                      />
                      {errors.duration &&
                        errors.duration.type === 'validate' && (
                          <Styled.ErrorMessage>
                            {'Duration cannot be less than 0.'}
                          </Styled.ErrorMessage>
                        )}
                    </>
                  )}
                />
              </Styled.InputWrapper>
            </Styled.PaperBodyOverload>
          </Styled.Paper>
          <Styled.ActionButtonContainer>
            <FilledButton
              variant={'primary'}
              data-testid={'edit-button'}
              type={'submit'}
              onClick={handleSubmit(onSubmit)}
              isDisabled={isSubmittingForm}>
              {'Save'}
            </FilledButton>
            <Styled.CancelButton
              variant={'slighter'}
              type={'none'}
              onClick={onBack}
              isDisabled={isSubmittingForm}>
              {'Cancel'}
            </Styled.CancelButton>
          </Styled.ActionButtonContainer>
        </Styled.FormContainer>
      </PageWrapper>
    )
  );
};

export default SuspensionRuleDetail;
